import {
  type Filament,
  type PackagingKit,
  type PricingCalculateResponse,
  type Printer,
  type Settings,
  type Supply,
  formatBRL,
} from '@deizi/shared';
import { useState } from 'react';
import { AffixInput, Field, Seg, Toggle } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Sheet } from '../../components/Overlays';
import { fileContentUrl } from '../../lib/files-api';
import { formatGrams, formatHm, parseDecimal } from '../../lib/numbers';
import { useIsPhone } from '../../lib/use-media';
import {
  type Draft,
  type PlateDraft,
  type SlotDraft,
  defaultKitId,
  describe,
  emptySlot,
  settingsPct,
  slotLabel,
} from './draft';
import {
  FilamentPicker,
  PrinterPicker,
  SupplyPicker,
  Swatch,
  filamentSub,
  printerSub,
} from './Pickers';
import { Combobox } from '../../components/Combobox';
import { kitOption } from '../../components/pickers';

export interface Catalog {
  printers: Printer[];
  filaments: Filament[];
  kits: PackagingKit[];
  supplies: Supply[];
  settings: Settings | undefined;
}

type Update = (fn: (d: Draft) => Draft) => void;

/** Applies a change to one plate (file plates by index, or the manual plate). */
function patchPlate(d: Draft, index: number, fn: (p: PlateDraft) => PlateDraft): Draft {
  if (d.mode === 'manual') return { ...d, manualPlate: fn(d.manualPlate) };
  return { ...d, filePlates: d.filePlates.map((p) => (p.index === index ? fn(p) : p)) };
}

function patchSlot(d: Draft, plate: number, key: string, patch: Partial<SlotDraft>): Draft {
  return patchPlate(d, plate, (p) => ({
    ...p,
    slots: p.slots.map((s) => (s.key === key ? { ...s, ...patch } : s)),
  }));
}

function SlotRow({
  slot,
  i,
  plate,
  draft,
  update,
  filaments,
  onPick,
}: {
  slot: SlotDraft;
  i: number;
  plate: PlateDraft;
  draft: Draft;
  update: Update;
  filaments: Filament[];
  onPick: () => void;
}) {
  const phone = useIsPhone();
  const f = filaments.find((x) => x.id === slot.filamentId);
  const label = slotLabel(slot, i, draft.mode);
  const gid = `g-${slot.key}`;
  const canRm = draft.mode === 'manual' && plate.slots.length > 1;
  const picker = (
    <button
      type="button"
      className="picker"
      aria-haspopup="dialog"
      aria-label={`Filamento do ${label.toLowerCase()}: ${f ? `${f.name}, ${formatBRL(f.pricePerKgCentavos)}/kg` : 'escolher'}. Trocar`}
      onClick={onPick}
    >
      <Swatch color={f?.colorHex ?? slot.color} />
      <span className="pk-main">
        <span className="trunc" title={f?.name}>
          {f ? f.name : slot.type ? `Escolher ${slot.type}` : 'Escolher filamento'}
        </span>
        <span className="pk-sub">
          {f
            ? filamentSub(f)
            : slot.type
              ? `No arquivo: ${slot.type}${slot.color ? ` ${slot.color}` : ''}`
              : 'Preço por kg do catálogo'}
        </span>
      </span>
      <Icon name="chevronRight" size={18} />
    </button>
  );
  const nozzle =
    draft.mode === 'file' && !slot.nozzleFromFile ? (
      <Seg
        label={`Bico do ${label.toLowerCase()}`}
        value={slot.nozzle}
        options={[
          { value: 'main', label: 'Bico principal' },
          { value: 'aux', label: 'Bico auxiliar' },
        ]}
        onChange={(v) => update((d) => patchSlot(d, plate.index, slot.key, { nozzle: v }))}
      />
    ) : null;
  const grams = (
    <div className="input" style={{ height: phone ? 40 : 52 }}>
      <input
        id={gid}
        className="qin"
        inputMode="decimal"
        aria-label={phone ? `Gramas do ${label.toLowerCase()}` : undefined}
        placeholder="0"
        value={slot.grams}
        onChange={(e) =>
          update((d) => patchSlot(d, plate.index, slot.key, { grams: e.target.value }))
        }
      />
      <span className="unit">g</span>
    </div>
  );
  const remove = canRm ? (
    <button
      type="button"
      className="btn icon sm g"
      style={{ width: 36 }}
      aria-label={`Remover ${label.toLowerCase()}`}
      onClick={() =>
        update((d) =>
          patchPlate(d, plate.index, (p) => ({
            ...p,
            slots: p.slots.filter((s) => s.key !== slot.key),
          })),
        )
      }
    >
      <Icon name="close" size={16} />
    </button>
  ) : null;

  if (phone) {
    return (
      <div className="card sunk col" style={{ gap: 8, padding: 12, borderRadius: 12 }}>
        <div className="row between">
          <span className="sm" style={{ fontWeight: 500 }}>
            {label}
          </span>
          {remove}
        </div>
        {picker}
        {grams}
        {nozzle}
      </div>
    );
  }
  return (
    <div className="field">
      <label htmlFor={gid}>{label}</label>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `minmax(0,1fr) 88px${canRm ? ' 36px' : ''}`,
          gap: 8,
          alignItems: 'center',
        }}
      >
        {picker}
        {grams}
        {remove}
      </div>
      {nozzle}
    </div>
  );
}

function PlateFields({ plate, update }: { plate: PlateDraft; update: Update }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
      <Field id={`t-${plate.index}`} label="Tempo da placa (h:mm)">
        <AffixInput
          id={`t-${plate.index}`}
          value={plate.time}
          placeholder="0:00"
          inputMode="text"
          onChange={(v) => update((d) => patchPlate(d, plate.index, (p) => ({ ...p, time: v })))}
        />
      </Field>
      <Field id={`pp-${plate.index}`} label="Unidades por placa">
        <AffixInput
          id={`pp-${plate.index}`}
          value={plate.unitsPerPlate}
          inputMode="numeric"
          onChange={(v) =>
            update((d) => patchPlate(d, plate.index, (p) => ({ ...p, unitsPerPlate: v })))
          }
        />
      </Field>
    </div>
  );
}

/** "Impressão" card: printer, plates and slots (file) or materials (manual), time, quantity. */
function PrintCard({
  draft,
  update,
  catalog,
  result,
}: {
  draft: Draft;
  update: Update;
  catalog: Catalog;
  result: PricingCalculateResponse | undefined;
}) {
  const phone = useIsPhone();
  const [picking, setPicking] = useState<{ plate: number; key: string } | 'printer' | null>(null);
  const printer = catalog.printers.find((p) => p.id === draft.printerId);
  const plates = draft.mode === 'file' ? draft.filePlates : [draft.manualPlate];
  const multi = plates.length > 1;
  const pickingSlot =
    picking && picking !== 'printer'
      ? plates.find((p) => p.index === picking.plate)?.slots.find((s) => s.key === picking.key)
      : undefined;
  const pickingIdx =
    picking && picking !== 'printer'
      ? (plates
          .find((p) => p.index === picking.plate)
          ?.slots.findIndex((s) => s.key === picking.key) ?? 0)
      : 0;

  const slots = (plate: PlateDraft) =>
    plate.slots.map((s, i) => (
      <SlotRow
        key={s.key}
        slot={s}
        i={i}
        plate={plate}
        draft={draft}
        update={update}
        filaments={catalog.filaments}
        onPick={() => setPicking({ plate: plate.index, key: s.key })}
      />
    ));

  return (
    <section className="card col" style={{ gap: 14, padding: 16 }}>
      <h2 style={{ fontSize: 18 }}>{phone ? 'Impressora e materiais' : 'Impressão'}</h2>
      <div className="field">
        <span className="lbl" id="imp-l">
          Impressora
        </span>
        <button
          type="button"
          className="picker"
          aria-haspopup="dialog"
          aria-labelledby="imp-l"
          onClick={() => setPicking('printer')}
        >
          <Icon name="printer" size={20} />
          <span className="pk-main">
            <span className="trunc">
              {printer ? `${printer.name} · ${printer.model}` : 'Escolher impressora'}
            </span>
            <span className="pk-sub">
              {printer ? printerSub(printer) : 'Custo da máquina por hora'}
            </span>
          </span>
          <Icon name="chevronRight" size={18} />
        </button>
      </div>

      {multi
        ? plates.map((plate) => (
            <div
              key={plate.index}
              className="card sunk col"
              style={{ gap: 12, padding: 12, borderRadius: 12, opacity: plate.included ? 1 : 0.7 }}
            >
              <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={plate.included}
                  onChange={(e) =>
                    update((d) =>
                      patchPlate(d, plate.index, (p) => ({ ...p, included: e.target.checked })),
                    )
                  }
                />
                {plate.thumbnailFileId ? (
                  <img
                    className="thumb"
                    src={fileContentUrl(plate.thumbnailFileId)}
                    alt=""
                    width={40}
                    height={40}
                  />
                ) : null}
                <span className="grow" style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, display: 'block' }} className="trunc">
                    Placa {plate.index}
                    {plate.name ? ` · ${plate.name}` : ''}
                  </span>
                  <span className="xs muted">
                    {plate.included ? 'Entra no custo' : 'Fora deste orçamento'}
                  </span>
                </span>
              </label>
              {plate.included ? (
                <>
                  {slots(plate)}
                  <PlateFields plate={plate} update={update} />
                </>
              ) : null}
            </div>
          ))
        : plates.map((plate) => (
            <div key={plate.index} className="col" style={{ gap: 14 }}>
              {slots(plate)}
              {draft.mode === 'manual' ? (
                <button
                  type="button"
                  className="btn sm g"
                  style={{ alignSelf: 'flex-start', padding: 0 }}
                  onClick={() =>
                    update((d) => ({
                      ...d,
                      manualPlate: {
                        ...d.manualPlate,
                        slots: [...d.manualPlate.slots, emptySlot()],
                      },
                    }))
                  }
                >
                  <Icon name="plus" size={16} />
                  {phone ? 'Outro material' : 'Material'}
                </button>
              ) : null}
              {phone ? null : <PlateFields plate={plate} update={update} />}
            </div>
          ))}

      {phone ? null : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
          <Field id="un" label="Quantidade">
            <AffixInput
              id="un"
              value={draft.quantity}
              unit="un."
              inputMode="numeric"
              onChange={(v) => update((d) => ({ ...d, quantity: v }))}
            />
          </Field>
          <Field label="Por unidade">
            <div className="input readonly">
              <span className="tab">
                {result
                  ? `${formatGrams(result.perUnit.grams)} · ${formatHm(result.perUnit.printSeconds)}`
                  : '—'}
              </span>
            </div>
          </Field>
        </div>
      )}

      {picking === 'printer' ? (
        <PrinterPicker
          printers={catalog.printers}
          selectedId={draft.printerId}
          onClose={() => setPicking(null)}
          onChoose={(p) => {
            update((d) => ({ ...d, printerId: p.id }));
            setPicking(null);
          }}
        />
      ) : null}
      {picking && picking !== 'printer' && pickingSlot ? (
        <FilamentPicker
          title={`Filamento do ${slotLabel(pickingSlot, pickingIdx, draft.mode).split(' · ')[0]?.toLowerCase()}`}
          filaments={catalog.filaments}
          selectedId={pickingSlot.filamentId}
          material={pickingSlot.type}
          onClose={() => setPicking(null)}
          onChoose={(f) => {
            update((d) => patchSlot(d, picking.plate, picking.key, { filamentId: f.id }));
            setPicking(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** Phone "Quantidade" card with a stepper (board QQ-Celular-Arquivo). */
function PhoneQuantity({
  draft,
  update,
  result,
}: {
  draft: Draft;
  update: Update;
  result: PricingCalculateResponse | undefined;
}) {
  const plate =
    draft.mode === 'manual' ? draft.manualPlate : draft.filePlates.find((p) => p.included);
  const single = draft.mode === 'manual' || draft.filePlates.filter((p) => p.included).length === 1;
  const step = (n: number) =>
    update((d) => ({ ...d, quantity: String(Math.max(1, (parseDecimal(d.quantity) ?? 1) + n)) }));
  return (
    <section className="card col" style={{ gap: 12, padding: 16 }}>
      <h2 style={{ fontSize: 18 }}>Quantidade</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field">
          <label htmlFor="q">Unidades</label>
          <div className="row" style={{ gap: 6 }}>
            <button
              type="button"
              className="btn icon"
              aria-label="Menos uma unidade"
              onClick={() => step(-1)}
            >
              −
            </button>
            <input
              id="q"
              className="input tab"
              inputMode="numeric"
              value={draft.quantity}
              onChange={(e) => update((d) => ({ ...d, quantity: e.target.value }))}
              style={{ textAlign: 'center', padding: '0 4px', minWidth: 0 }}
            />
            <button
              type="button"
              className="btn icon"
              aria-label="Mais uma unidade"
              onClick={() => step(1)}
            >
              +
            </button>
          </div>
        </div>
        {single && plate ? (
          <Field id="up" label="Por placa">
            <input
              id="up"
              className="input tab"
              inputMode="numeric"
              value={plate.unitsPerPlate}
              onChange={(e) =>
                update((d) =>
                  patchPlate(d, plate.index, (p) => ({ ...p, unitsPerPlate: e.target.value })),
                )
              }
            />
          </Field>
        ) : null}
      </div>
      {single && plate && draft.mode === 'manual' ? (
        <Field id="mt" label="Tempo da placa (h:mm)">
          <input
            id="mt"
            className="input tab"
            placeholder="0:00"
            value={plate.time}
            onChange={(e) =>
              update((d) => patchPlate(d, plate.index, (p) => ({ ...p, time: e.target.value })))
            }
          />
        </Field>
      ) : null}
      {result ? (
        <p className="sm muted">
          Por unidade:{' '}
          <b className="tab" style={{ color: 'var(--ink)', fontWeight: 500 }}>
            {formatGrams(result.perUnit.grams)} · {formatHm(result.perUnit.printSeconds)}
          </b>
          . Total: {formatHm(result.totals.printSeconds)} ·{' '}
          {result.totals.plates === 1 ? '1 placa' : `${result.totals.plates} placas`}.
        </p>
      ) : null}
    </section>
  );
}

function LaborCard({ draft, update, catalog }: { draft: Draft; update: Update; catalog: Catalog }) {
  const phone = useIsPhone();
  const designRate = catalog.settings?.costs.designRatePerHourCentavos;
  const kitId = draft.kitId ?? defaultKitId(catalog.kits);
  return (
    <section className="card col" style={{ gap: 14, padding: 16 }}>
      <h2 style={{ fontSize: 18 }}>
        {phone ? 'Mão de obra por unidade' : 'Mão de obra e embalagem'}
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
        <Field id="ac" label="Acabamento">
          <AffixInput
            id="ac"
            value={draft.finishingMin}
            unit={phone ? 'min' : 'min/un.'}
            onChange={(v) => update((d) => ({ ...d, finishingMin: v }))}
          />
        </Field>
        <Field id="em" label="Embalagem">
          <AffixInput
            id="em"
            value={draft.packingMin}
            unit={phone ? 'min' : 'min/un.'}
            onChange={(v) => update((d) => ({ ...d, packingMin: v }))}
          />
        </Field>
      </div>
      <Field id="pk" label="Kit de embalagem">
        <Combobox
          id="pk"
          value={kitId}
          options={[
            ...catalog.kits.map(kitOption),
            { value: '', label: 'Sem embalagem', pinned: 'bottom' },
          ]}
          onChange={(v) => update((d) => ({ ...d, kitId: v }))}
        />
      </Field>
      {phone ? null : (
        <Field
          id="dh"
          label="Projeto (horas de modelagem)"
          help={
            designRate ? `${formatBRL(designRate)} por hora, dividido pela quantidade.` : undefined
          }
        >
          <AffixInput
            id="dh"
            value={draft.designHours}
            placeholder="0"
            unit="h"
            onChange={(v) => update((d) => ({ ...d, designHours: v }))}
          />
        </Field>
      )}
    </section>
  );
}

function SuppliesCard({
  draft,
  update,
  catalog,
}: {
  draft: Draft;
  update: Update;
  catalog: Catalog;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card col" style={{ gap: 10, padding: 16 }}>
      <div className="row between">
        <h2 style={{ fontSize: 18 }}>Insumos</h2>
        <button type="button" className="btn sm" onClick={() => setOpen(true)}>
          <Icon name="plus" size={16} />
          Insumo
        </button>
      </div>
      {draft.supplies.length === 0 ? (
        <p className="sm muted">
          Nenhum insumo. Ímãs, argolas e insertos entram no custo por unidade.
        </p>
      ) : null}
      {draft.supplies.map((it, i) => {
        const s = catalog.supplies.find((x) => x.id === it.supplyId);
        if (!s) return null;
        return (
          <div
            key={it.supplyId}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) 64px 76px 36px',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <p className="sm trunc" style={{ fontWeight: 500 }}>
                {s.name}
              </p>
              <p className="xs muted">{formatBRL(s.unitCostCentavos)} cada</p>
            </div>
            <input
              className="input"
              style={{ height: 36, textAlign: 'right', padding: '0 8px' }}
              aria-label={`Quantidade de ${s.name} por unidade`}
              inputMode="decimal"
              value={it.qty}
              onChange={(e) =>
                update((d) => ({
                  ...d,
                  supplies: d.supplies.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                }))
              }
            />
            <span className="num sm tab" style={{ textAlign: 'right' }}>
              {formatBRL(Math.round(s.unitCostCentavos * (parseDecimal(it.qty) ?? 0)))}
            </span>
            <button
              type="button"
              className="btn icon sm g"
              style={{ width: 36 }}
              aria-label={`Remover ${s.name}`}
              onClick={() =>
                update((d) => ({ ...d, supplies: d.supplies.filter((_, j) => j !== i) }))
              }
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        );
      })}
      {open ? (
        <SupplyPicker
          supplies={catalog.supplies}
          chosen={draft.supplies}
          onClose={() => setOpen(false)}
          onAdd={(s) => {
            update((d) => {
              const have = d.supplies.find((x) => x.supplyId === s.id);
              return {
                ...d,
                supplies: have
                  ? d.supplies.map((x) =>
                      x.supplyId === s.id
                        ? { ...x, qty: String((parseDecimal(x.qty) ?? 0) + 1) }
                        : x,
                    )
                  : [...d.supplies, { supplyId: s.id, qty: '1' }],
              };
            });
            setOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

type AdjKey = 'marginPct' | 'failurePct' | 'rushPct' | 'discountPct';
const ADJ: { key: AdjKey; label: string; help: string }[] = [
  {
    key: 'marginPct',
    label: 'Margem alvo',
    help: 'Só neste orçamento. O padrão fica em Configurações.',
  },
  { key: 'failurePct', label: 'Taxa de falha', help: 'Reserva sobre material e máquina.' },
  { key: 'rushPct', label: 'Urgência', help: 'Aumenta o preço sugerido.' },
  { key: 'discountPct', label: 'Desconto', help: 'Diminui o preço sugerido.' },
];

function AdjustmentsCard({
  draft,
  update,
  catalog,
}: {
  draft: Draft;
  update: Update;
  catalog: Catalog;
}) {
  const phone = useIsPhone();
  const defaults = settingsPct(catalog.settings);
  const [editing, setEditing] = useState<AdjKey | null>(null);
  const [text, setText] = useState('');
  const value = (k: AdjKey) =>
    k === 'marginPct'
      ? (draft.marginPct ?? defaults.margin)
      : k === 'failurePct'
        ? (draft.failurePct ?? defaults.failure)
        : draft[k];
  const set = (k: AdjKey, v: string) => update((d) => ({ ...d, [k]: v }));

  if (phone) {
    const designRate = catalog.settings?.costs.designRatePerHourCentavos;
    const designOn = draft.designHours !== '';
    const current = ADJ.find((a) => a.key === editing);
    return (
      <section className="card col" style={{ gap: 4, padding: '8px 16px' }}>
        <div className="listrow">
          <span className="grow">Taxa de projeto</span>
          <Toggle
            label="Taxa de projeto"
            on={designOn}
            onChange={(on) => update((d) => ({ ...d, designHours: on ? '1' : '' }))}
          />
        </div>
        {designOn ? (
          <div
            className="col"
            style={{ gap: 6, padding: '4px 0 12px', borderBottom: '1px solid var(--line)' }}
          >
            <label htmlFor="dh" className="lbl">
              Horas de modelagem
            </label>
            <AffixInput
              id="dh"
              value={draft.designHours}
              unit="h"
              onChange={(v) => update((d) => ({ ...d, designHours: v }))}
            />
            <span className="help">
              {designRate ? `${formatBRL(designRate)} por hora, ` : ''}dividido pela quantidade.
            </span>
          </div>
        ) : null}
        {ADJ.map((a) => (
          <button
            key={a.key}
            type="button"
            className="listrow"
            onClick={() => {
              setText(value(a.key));
              setEditing(a.key);
            }}
          >
            <span className="grow">{a.label}</span>
            <span className="tab muted">{value(a.key) || '0'}%</span>
            <Icon name="chevronRight" size={18} />
          </button>
        ))}
        {current ? (
          <Sheet label={current.label} onClose={() => setEditing(null)}>
            <h2>{current.label}</h2>
            <Field id="nv" label={`${current.label} (%)`} help={current.help}>
              <AffixInput id="nv" value={text} unit="%" onChange={setText} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="button" className="btn" onClick={() => setEditing(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn p"
                onClick={() => {
                  set(current.key, text);
                  setEditing(null);
                }}
              >
                Aplicar
              </button>
            </div>
          </Sheet>
        ) : null}
      </section>
    );
  }

  return (
    <section className="card col" style={{ gap: 14, padding: 16 }}>
      <h2 style={{ fontSize: 18 }}>Ajustes</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 12 }}>
        {ADJ.map((a) => {
          const changed =
            (a.key === 'marginPct' &&
              draft.marginPct !== null &&
              draft.marginPct !== defaults.margin) ||
            (a.key === 'failurePct' &&
              draft.failurePct !== null &&
              draft.failurePct !== defaults.failure);
          return (
            <Field key={a.key} id={a.key} label={a.label}>
              <AffixInput
                id={a.key}
                value={value(a.key)}
                unit="%"
                changed={changed}
                onChange={(v) => set(a.key, v)}
              />
            </Field>
          );
        })}
      </div>
    </section>
  );
}

export function InputsPanel({
  draft,
  update,
  catalog,
  result,
  hasData,
  filePanel,
}: {
  draft: Draft;
  update: Update;
  catalog: Catalog;
  result: PricingCalculateResponse | undefined;
  hasData: boolean;
  filePanel: React.ReactNode;
}) {
  const phone = useIsPhone();
  return (
    <>
      <div
        className="seg"
        role="tablist"
        aria-label="Como informar a peça"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
      >
        {(['file', 'manual'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={draft.mode === m}
            className={draft.mode === m ? 'on' : ''}
            style={{ justifyContent: 'center' }}
            onClick={() => update((d) => ({ ...d, mode: m }))}
          >
            <Icon name={m === 'file' ? 'box' : 'calculator'} size={18} />
            {m === 'file' ? 'Arquivo' : 'Manual'}
          </button>
        ))}
      </div>
      {draft.mode === 'file' ? filePanel : null}
      <section className="card col" style={{ gap: 10, padding: 16 }}>
        <Field
          id="desc"
          label="Descrição"
          help={
            draft.mode === 'manual'
              ? 'Copie do fatiador: gramas de cada material e tempo da placa.'
              : 'Aparece no orçamento e na resposta ao cliente.'
          }
        >
          <input
            id="desc"
            className="input"
            placeholder={
              draft.mode === 'file' && draft.file ? describe(draft) : 'Ex.: Chaveiro Gato'
            }
            value={draft.description}
            onChange={(e) => update((d) => ({ ...d, description: e.target.value }))}
          />
        </Field>
      </section>
      {hasData ? (
        <>
          <PrintCard draft={draft} update={update} catalog={catalog} result={result} />
          {phone ? <PhoneQuantity draft={draft} update={update} result={result} /> : null}
          <LaborCard draft={draft} update={update} catalog={catalog} />
          <SuppliesCard draft={draft} update={update} catalog={catalog} />
          <AdjustmentsCard draft={draft} update={update} catalog={catalog} />
        </>
      ) : null}
    </>
  );
}
